import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, RotateCcw, Settings, CheckCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "The Heartbeat of Games",
        icon: "💓",
        content: (
            <div className="space-y-6">
                <div className="bg-sky-50 p-6 rounded-2xl border-2 border-sky-200">
                    <h3 className="font-bold text-xl text-sky-800 mb-3">60 Times Per Second</h3>
                    <p className="text-slate-700 mb-4">
                        Games aren't just one static picture. They are <strong>loops</strong> that run over and over, really fast.
                    </p>
                    <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-sky-100">
                        <div className="text-4xl">🔄</div>
                        <div className="text-sm text-slate-600">
                            <strong>1. Input:</strong> Did player press jump?<br />
                            <strong>2. Update:</strong> Move the characters.<br />
                            <strong>3. Draw:</strong> Show the new picture.
                        </div>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Frame Budget",
        icon: "💰",
        content: (
            <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-200">
                    <h3 className="font-bold text-xl text-green-800 mb-3">16 Milliseconds</h3>
                    <p className="text-slate-700 mb-4">
                        To run smoothly at 60 FPS (Frames Per Second), the computer must finish all its math in <strong>16ms</strong> or less!
                    </p>
                    <div className="w-full bg-slate-200 h-6 rounded-full overflow-hidden">
                        <div className="w-2/3 bg-green-500 h-full flex items-center justify-center text-xs text-white font-bold">Safe</div>
                        <div className="w-1/3 bg-red-500 h-full flex items-center justify-center text-xs text-white font-bold">Lag!</div>
                    </div>
                </div>
                <div className="bg-slate-100 p-4 rounded-xl border border-slate-200">
                    <p className="text-slate-600 text-sm">
                        If you add too many objects, the computer takes too long to calculate, and the game "lags" (stutters).
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "🎯",
        content: (
            <div className="space-y-6">
                <div className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-200">
                    <h3 className="font-bold text-xl text-amber-800 mb-3">Stress Test</h3>
                    <p className="text-slate-700 mb-4">
                        We need to test our game engine. Can it handle a crowd?
                    </p>
                    <p className="font-bold text-amber-900">
                        Goal: Add at least 10 Sprites to the simulation!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
interface Sprite {
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    emoji: string;
}

const LoopGame = ({ onComplete }: { onComplete: () => void }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [fps, setFps] = useState(60);
    const [showFps, setShowFps] = useState(true);
    const [gravity, setGravity] = useState(0.5);
    const [bounce, setBounce] = useState(0.8);
    const [sprites, setSprites] = useState<Sprite[]>([]);
    const [frameCount, setFrameCount] = useState(0);
    const animationRef = useRef<number>();
    const lastTimeRef = useRef<number>(0);

    // Initialize sprites
    useEffect(() => {
        setSprites([
            { x: 100, y: 50, vx: 3, vy: 0, size: 40, emoji: '🦍' },
            { x: 200, y: 100, vx: -2, vy: 1, size: 30, emoji: '🍌' },
            { x: 300, y: 150, vx: 1, vy: -2, size: 35, emoji: '🌴' },
        ]);
    }, []);

    // Game loop
    useEffect(() => {
        if (!isRunning || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d')!;
        const targetFrameTime = 1000 / fps;

        const gameLoop = (currentTime: number) => {
            const deltaTime = currentTime - lastTimeRef.current;

            if (deltaTime >= targetFrameTime) {
                lastTimeRef.current = currentTime;
                setFrameCount(f => f + 1);

                // Clear
                ctx.fillStyle = '#e0f2fe';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Draw ground
                ctx.fillStyle = '#22c55e';
                ctx.fillRect(0, canvas.height - 40, canvas.width, 40);

                // Update and draw sprites
                setSprites(prev => prev.map(sprite => {
                    let { x, y, vx, vy, size, emoji } = sprite;

                    // Apply gravity
                    vy += gravity;

                    // Move
                    x += vx;
                    y += vy;

                    // Bounce off walls
                    if (x < size / 2 || x > canvas.width - size / 2) {
                        vx *= -bounce;
                        x = Math.max(size / 2, Math.min(canvas.width - size / 2, x));
                    }

                    // Bounce off ground
                    if (y > canvas.height - 40 - size / 2) {
                        vy *= -bounce;
                        y = canvas.height - 40 - size / 2;
                        vx *= 0.99; // Friction
                    }

                    // Bounce off ceiling
                    if (y < size / 2) {
                        vy *= -bounce;
                        y = size / 2;
                    }

                    // Draw
                    ctx.font = `${size}px Arial`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(emoji, x, y);

                    return { x, y, vx, vy, size, emoji };
                }));

                // Draw FPS counter
                if (showFps) {
                    ctx.fillStyle = '#1e293b';
                    ctx.font = '14px monospace';
                    ctx.textAlign = 'left';
                    ctx.fillText(`FPS: ${fps} | Frame: ${frameCount}`, 10, 20);
                }
            }

            animationRef.current = requestAnimationFrame(gameLoop);
        };

        animationRef.current = requestAnimationFrame(gameLoop);

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [isRunning, fps, gravity, bounce, showFps, frameCount]);

    const toggleRunning = () => {
        setIsRunning(!isRunning);
    };

    const reset = () => {
        setIsRunning(false);
        setFrameCount(0);
        setSprites([
            { x: 100, y: 50, vx: 3, vy: 0, size: 40, emoji: '🦍' },
            { x: 200, y: 100, vx: -2, vy: 1, size: 30, emoji: '🍌' },
            { x: 300, y: 150, vx: 1, vy: -2, size: 35, emoji: '🌴' },
        ]);
    };

    const addSprite = () => {
        const emojis = ['🦍', '🍌', '🥥', '🌴', '🐒', '🦜'];
        setSprites(prev => {
            const newSprites = [...prev, {
                x: Math.random() * 300 + 50,
                y: 50,
                vx: (Math.random() - 0.5) * 6,
                vy: 0,
                size: Math.random() * 20 + 25,
                emoji: emojis[Math.floor(Math.random() * emojis.length)],
            }];

            if (newSprites.length >= 10) {
                onComplete();
            }

            return newSprites;
        });
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Game Canvas */}
            <div className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl">
                <canvas
                    ref={canvasRef}
                    width={500}
                    height={350}
                    className="w-full rounded-2xl border-4 border-sky-200"
                />

                <div className="flex justify-center gap-4 mt-6">
                    <button
                        onClick={toggleRunning}
                        className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 ${isRunning ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                            } text-white`}
                    >
                        {isRunning ? <><Pause size={18} /> Pause</> : <><Play size={18} /> Play</>}
                    </button>
                    <button onClick={reset} className="bg-slate-200 text-slate-600 px-6 py-3 rounded-xl font-bold hover:bg-slate-300 flex items-center gap-2">
                        <RotateCcw size={18} /> Reset
                    </button>
                    <button onClick={addSprite} className="bg-amber-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-amber-600">
                        + Add Sprite
                    </button>
                </div>
            </div>

            {/* Controls */}
            <div className="space-y-6">
                <div className="bg-white rounded-3xl p-6 shadow-xl">
                    <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                        <Settings className="text-sky-500" /> Settings
                    </h3>

                    {/* FPS */}
                    <div className="mb-4">
                        <div className="flex justify-between mb-2">
                            <span className="font-bold text-sm">Frames Per Second</span>
                            <span className="font-mono text-sky-600">{fps}</span>
                        </div>
                        <input
                            type="range" min="10" max="60" value={fps}
                            onChange={e => setFps(parseInt(e.target.value))}
                            className="w-full accent-sky-500"
                        />
                        <div className="flex justify-between text-xs text-slate-400">
                            <span>Slow</span>
                            <span>Fast</span>
                        </div>
                    </div>

                    {/* Gravity */}
                    <div className="mb-4">
                        <div className="flex justify-between mb-2">
                            <span className="font-bold text-sm">Gravity</span>
                            <span className="font-mono text-sky-600">{gravity.toFixed(1)}</span>
                        </div>
                        <input
                            type="range" min="0" max="2" step="0.1" value={gravity}
                            onChange={e => setGravity(parseFloat(e.target.value))}
                            className="w-full accent-sky-500"
                        />
                    </div>

                    {/* Bounce */}
                    <div className="mb-4">
                        <div className="flex justify-between mb-2">
                            <span className="font-bold text-sm">Bounciness</span>
                            <span className="font-mono text-sky-600">{bounce.toFixed(1)}</span>
                        </div>
                        <input
                            type="range" min="0" max="1" step="0.1" value={bounce}
                            onChange={e => setBounce(parseFloat(e.target.value))}
                            className="w-full accent-sky-500"
                        />
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={showFps} onChange={e => setShowFps(e.target.checked)} className="accent-sky-500" />
                        <span className="text-sm">Show FPS Counter</span>
                    </label>
                </div>

                {/* Explanation */}
                <div className="bg-sky-50 rounded-2xl p-6 border-2 border-sky-200">
                    <h3 className="font-bold text-sky-800 mb-2">🎓 What's Happening?</h3>
                    <ul className="text-sky-700 text-sm space-y-2">
                        <li><strong>1. Update:</strong> Move each sprite</li>
                        <li><strong>2. Check:</strong> Did it hit a wall?</li>
                        <li><strong>3. Draw:</strong> Show everything</li>
                        <li><strong>4. Repeat!</strong> (60 times a second)</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const GameLoop = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        if (!completed) {
            setCompleted(true);
            completeModule('game-loop', 100);
        }
    };

    return (
        <LessonWrapper
            moduleName="Game Loop"
            moduleEmoji="🔄"
            moduleColor="sky"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Simulator"
        >
            <LoopGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default GameLoop;
